import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Accordion, Pills, SkeletonLoading } from '@saleshandy/design-system';
import { Minus, Plus, Filter } from '@saleshandy/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DateTime } from 'luxon';
import Button from '../../../../shared/design-system/components/atoms/button';
import KeywordInputComponent from './keyword-search-input/keyword-search-input';
import Tag from '../../../../shared/design-system/components/tag/tag';
import {
  useSavedLeadFilter,
  FILTER_SAVED_LIST,
} from '../hooks/useLeadSavedFilter';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import {
  FilterComponentType,
  LeadFinderSecondaryTab,
  SavedTabFilters,
  LeadFinderListVisibility,
  LeadFinderPrimaryTab,
} from '../../types';
import DateFilter from '../../../../shared/design-system/components/molecules/leads-filter/date-filter';
import TagSavedFilters from './tags/tag-saved-filter';
import SavedList from './saved-list/saved-list';
import Checkbox from '../../../../shared/design-system/components/atoms/checkbox';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';

export type SavedListData = {
  id: number;
  label: string;
  visibility: LeadFinderListVisibility;
  count?: number;
  isDefault?: boolean;
  owner: {
    firstName: string;
    lastName: string;
  };
};

export type V2SavedTabProps = {
  leadTags: any[];
  isLeadTagsLoading: boolean;
  isSavedTabLoading: boolean;
  activeSubTab: LeadFinderSecondaryTab;
  savedFilters: SavedTabFilters;
  setSavedFilters: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  savedListsData?: SavedListData[];
  onEditList?: (listId: number, listName: string) => void;
  onExportList?: (listId: number, listName: string) => void;
  onDeleteList?: (listId: number) => Promise<void>;
  onCreateList?: (
    listName: string,
    isShared: boolean,
    teamIds: number[],
  ) => Promise<void>;
  onUpdateList?: (
    listId: number | null,
    listName: string,
    isShared: boolean,
    teamIds: number[],
  ) => Promise<void>;
  teamsList?: any[];
  getTeamsListRequestStatus?: any;
  sendGetTeamsListRequest?: VoidFunction;
  isUpdateListLoading?: boolean;
  activeLeadType?: LeadFinderPrimaryTab;
  allListsData?: SavedListData[];
};

const V2SavedTab: React.FC<V2SavedTabProps> = ({
  leadTags,
  isLeadTagsLoading,
  isSavedTabLoading,
  activeSubTab,
  savedFilters,
  setSavedFilters,
  savedListsData = [],
  onEditList,
  onExportList,
  onDeleteList,
  onCreateList,
  onUpdateList,
  teamsList = [],
  getTeamsListRequestStatus,
  sendGetTeamsListRequest = () => {},
  isUpdateListLoading = false,
  activeLeadType,
  allListsData = [],
}) => {
  const location = useLocation();
  const keywordInputRef: any = useRef();

  const {
    handleQuery,
    handleIndividualTag,
    handleClearAllFilters,
    handleExcludeProspectsToggle,
    handleClearAdvanceFilters,
  } = useSavedLeadFilter();

  const [selectedFilter, setSelectedFilter] = useState<any>([]);

  const overlayRef = useRef(null);
  const accordionKey = 3830;
  const [showTagTooltip, setShowTagTooltip] = useState<any>(null);

  // Filter out invalid tag IDs that don't exist in leadTags
  const validTagIds = useMemo(() => {
    if (
      !leadTags ||
      leadTags?.length === 0 ||
      !savedFilters?.tags ||
      savedFilters?.tags?.length === 0
    ) {
      return savedFilters?.tags || [];
    }

    const availableTagIds = leadTags?.map((tag) => tag?.id);
    const filteredTags = savedFilters?.tags?.filter((tagId) =>
      availableTagIds?.includes(tagId),
    );

    if (filteredTags?.length !== savedFilters?.tags?.length) {
      setTimeout(() => {
        setSavedFilters((prevFilters) => ({
          ...prevFilters,
          tags: filteredTags,
        }));

        handleQuery({
          filterKey: 'tags',
          filterValue: {
            tags: filteredTags,
          },
          filterType: FilterComponentType.TAG_CHECKBOX,
        });
      }, 0);
    }

    return filteredTags;
  }, [leadTags, savedFilters.tags, setSavedFilters, handleQuery]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    count += validTagIds?.length || 0;
    if (savedFilters?.createdDate && savedFilters.createdDate.length === 2) {
      count += 1;
    }
    if (savedFilters?.excludeProspects) {
      count += 1;
    }
    count += savedFilters?.emailStatus?.length || 0;

    const params = new URLSearchParams(location?.search || '');
    const listKey =
      activeLeadType === LeadFinderPrimaryTab.COMPANY
        ? 'company_list'
        : 'people_list';
    const excludeListKey =
      activeLeadType === LeadFinderPrimaryTab.COMPANY
        ? 'excludeCompany_list'
        : 'excludePeople_list';
    const parseListIds = (str: string | null): number => {
      if (!str?.trim()) return 0;
      return str
        .split(',')
        .map((id) => parseInt(id.replace(/"/g, '').trim(), 10))
        .filter((id) => !Number.isNaN(id) && id > 0).length;
    };
    count += parseListIds(params.get(listKey));
    count += parseListIds(params.get(excludeListKey));

    return count;
  }, [
    validTagIds,
    savedFilters?.createdDate,
    savedFilters?.excludeProspects,
    savedFilters?.emailStatus,
    location?.search,
    activeLeadType,
  ]);

  const onSelectFilter = (id, filter) => {
    setSelectedFilter((prevSelectedFilter) => {
      const index = prevSelectedFilter.findIndex((ele) => ele.id === id);
      if (index === -1) {
        return [
          ...prevSelectedFilter,
          {
            id,
            filter,
          },
        ];
      }
      return prevSelectedFilter.filter((ele) => ele.id !== id);
    });
  };

  const isSelectedFilter = useMemo(
    () => (filter) => selectedFilter.some((f) => f.id === filter.id),
    [selectedFilter],
  );

  const handleDeleteTagFilter = (filter) => {
    if (filter.key === 'createdDate') {
      setSavedFilters((prevFilters) => ({
        ...prevFilters,
        createdDate: [],
      }));
      handleQuery({
        filterKey: filter.key,
        filterValue: {
          createdDate: [],
        },
        filterType: FilterComponentType.SAVED_LEAD_DATE,
      });
    } else if (filter.key === 'tags') {
      setSavedFilters((prevFilters) => ({
        ...prevFilters,
        tags: [],
      }));
      handleQuery({
        filterKey: filter.key,
        filterValue: {
          tags: [],
        },
        filterType: FilterComponentType.TAG_CHECKBOX,
      });
    } else if (filter.key === 'excludeProspects') {
      setSavedFilters((prevFilters) => ({
        ...prevFilters,
        excludeProspects: false,
        emailStatus: [],
      }));
      // Remove both advance filters in a single navigation to avoid duplicate
      // counts/leads API calls from two back-to-back history pushes (BE-2578).
      handleClearAdvanceFilters();
    }
  };

  useEffect(() => {
    if (keywordInputRef?.current) {
      keywordInputRef?.current?.focus();
    }
    return () => {
      setSelectedFilter([]);
    };
  }, []);

  const getLebelTooltip = (text: string) => (
    <Tooltip id="popover-basic" className="bs-tooltip-inner">
      {text}
    </Tooltip>
  );

  const handleDateChange = (createdDates: Date[]) => {
    const dates = createdDates?.filter((e) => !!e);
    setSavedFilters({
      ...savedFilters,
      createdDate: dates,
    });

    if (dates?.length === 0) {
      handleQuery({
        filterKey: 'createdDate',
        filterValue: {
          createdDate: [],
        },
        filterType: FilterComponentType.SAVED_LEAD_DATE,
      });
      return;
    }

    if (dates?.length < 2) {
      return;
    }

    setSavedFilters({
      ...savedFilters,
      createdDate: dates,
    });

    const dateTimes = [
      DateTime.fromJSDate(dates[0]),
      DateTime.fromJSDate(dates[1]),
    ];

    handleQuery({
      filterKey: 'createdDate',
      filterValue: {
        createdDate: [
          dateTimes[0].toFormat('dd MMM, yy'),
          dateTimes[1].toFormat('dd MMM, yy'),
        ],
      },
      filterType: FilterComponentType.SAVED_LEAD_DATE,
    });
  };

  return (
    <>
      <KeywordInputComponent
        handleQuery={handleQuery}
        keywordInputRef={keywordInputRef}
        activeSubTab={activeSubTab}
      />
      <h3 className="v2-leads-filter">
        <div className="d-flex align-items-center gap-8">
          <div>
            <Filter className="sh-filter" />
            Filters
          </div>
          {appliedFilterCount > 0 && (
            <span className="v2-leads-filter-count">{appliedFilterCount}</span>
          )}
        </div>
        <div className="v2-leads-filter-actions">
          {appliedFilterCount > 0 && (
            <>
              <Button
                variant={'link' as any}
                onClick={handleClearAllFilters}
                className={`clear-all-button ${
                  isSavedTabLoading ? 'is-disabled' : ''
                }`}
                disabled={isSavedTabLoading}
              >
                Clear All
              </Button>
            </>
          )}
        </div>
      </h3>
      <div className="d-flex flex-column overflow-y-auto">
        <div
          key={accordionKey}
          ref={overlayRef}
          className="v2-people-tab-accordian"
        >
          <Accordion allowMultipleExpanded key={accordionKey} allowZeroExpanded>
            {FILTER_SAVED_LIST &&
              FILTER_SAVED_LIST.map((filter: any) => {
                if (
                  filter.peopleOnly &&
                  activeLeadType !== LeadFinderPrimaryTab.PEOPLE
                ) {
                  return null;
                }
                // Get applied filter tags for this specific filter
                const allTags = new Set();
                if (filter.key === 'tags' && validTagIds?.length > 0) {
                  validTagIds.forEach((tagId) => {
                    const tagObj = leadTags?.find((t) => t.id === tagId);
                    if (tagObj) {
                      allTags.add({
                        key: 'tags',
                        value: tagId,
                        label: tagObj.name,
                      });
                    }
                  });
                } else if (
                  filter.key === 'createdDate' &&
                  savedFilters.createdDate?.length === 2 &&
                  !!savedFilters.createdDate?.[0] &&
                  !!savedFilters.createdDate?.[1]
                ) {
                  const [startDate, endDate] = savedFilters.createdDate;
                  const formatDate = (d: Date) =>
                    DateTime.fromJSDate(d).toFormat('MMM d, yyyy');
                  allTags.add({
                    key: 'createdDate',
                    value: savedFilters.createdDate,
                    label:
                      startDate && endDate
                        ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                        : 'Date Range',
                  });
                } else if (filter.key === 'excludeProspects') {
                  if (savedFilters.excludeProspects) {
                    allTags.add({
                      key: 'excludeProspects',
                      value: 'true',
                      label: 'Exclude existing prospects',
                    });
                  }
                  if (savedFilters.emailStatus?.length > 0) {
                    const labelMap: Record<string, string> = {
                      valid: 'Valid',
                      risky: 'Risky',
                      bad: 'Bad',
                    };
                    savedFilters.emailStatus.forEach((v) => {
                      allTags.add({
                        key: 'emailStatus',
                        value: v,
                        label: labelMap[v] || v,
                      });
                    });
                  }
                }
                return (
                  <Accordion.Item
                    key={filter.id}
                    uuid={filter.id}
                    dangerouslySetExpanded={selectedFilter?.some(
                      (f) => f?.id === filter?.id,
                    )}
                    className={`${
                      isSelectedFilter(filter)
                        ? 'accordion__item_selected'
                        : 'accordion__item'
                    }`}
                  >
                    <Accordion.ItemHeading
                      onClick={() => onSelectFilter(filter.id, filter)}
                      className={`${
                        allTags.size > 0 && !isSelectedFilter(filter)
                          ? 'accordion-item-heading-tag'
                          : 'accordion-item-heading'
                      } ${allTags.size ? 'accordion-item-selected-tag' : ''}`}
                    >
                      <Accordion.ItemButton className="::before:none w-100">
                        <div className="w-100 accordian-label">
                          <div className="d-flex">
                            <div className="icon">{filter.icon}</div>
                            <div className="label">{filter?.label}</div>
                          </div>
                          <div className="d-flex align-items-center">
                            {(() => {
                              let tagCount = 0;
                              if (filter?.label === 'Tags') {
                                tagCount = validTagIds?.length || 0;
                              } else if (filter?.label === 'Date') {
                                tagCount =
                                  savedFilters.createdDate?.length === 2
                                    ? 1
                                    : 0;
                              } else if (filter?.label === 'Company List') {
                                tagCount =
                                  (savedFilters.listIds?.includes?.length ||
                                    0) +
                                  (savedFilters.listIds?.excludes?.length || 0);
                              } else if (filter?.label === 'Advance Filter') {
                                tagCount =
                                  (savedFilters?.emailStatus?.length || 0) +
                                  (savedFilters?.excludeProspects ? 1 : 0);
                              }
                              return tagCount > 0;
                            })() && (
                              <Tag
                                value={(() => {
                                  if (filter?.label === 'Tags') {
                                    return validTagIds?.length || 0;
                                  }
                                  if (filter?.label === 'Date') {
                                    return savedFilters.createdDate?.length ===
                                      2
                                      ? 1
                                      : 0;
                                  }
                                  if (filter?.label === 'Company List') {
                                    return (
                                      (savedFilters.listIds?.includes?.length ||
                                        0) +
                                      (savedFilters.listIds?.excludes?.length ||
                                        0)
                                    );
                                  }
                                  if (filter?.label === 'Advance Filter') {
                                    return (
                                      (savedFilters?.emailStatus?.length || 0) +
                                      (savedFilters?.excludeProspects ? 1 : 0)
                                    );
                                  }
                                  return 0;
                                })()}
                                variant={Tag.Variant.Remove}
                                className="bg-white rounded-pill"
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  handleDeleteTagFilter(filter);
                                }}
                              />
                            )}
                            <div className="d-flex align-items-center justify-content-end">
                              <Accordion.ItemState>
                                {({ expanded }) =>
                                  expanded ? (
                                    <Minus className="accordion-minus-icon" />
                                  ) : (
                                    <Plus className="accordion-plus-icon" />
                                  )
                                }
                              </Accordion.ItemState>
                            </div>
                          </div>
                        </div>
                        {allTags.size > 0 && !isSelectedFilter(filter) && (
                          <div className="filter-applied-collapse">
                            {Array.from(allTags)
                              .slice(0, 5)
                              .map((tag: any) => (
                                <span
                                  key={tag?.label}
                                  onMouseEnter={() =>
                                    setShowTagTooltip(tag?.label)
                                  }
                                  onMouseLeave={() => setShowTagTooltip(null)}
                                >
                                  <OverlayTrigger
                                    placement={Placement.BottomStart}
                                    overlay={getLebelTooltip(tag?.label)}
                                    show={
                                      showTagTooltip === tag?.label &&
                                      tag?.label?.length > 15
                                    }
                                  >
                                    <Tag
                                      key={tag?.label}
                                      value={
                                        tag?.label.length > 15
                                          ? `${tag?.label.substring(0, 15)}..`
                                          : tag?.label
                                      }
                                      variant={Tag.Variant.Remove}
                                      className="filter-tag"
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        handleIndividualTag(tag);
                                      }}
                                    />
                                  </OverlayTrigger>
                                </span>
                              ))}
                            {Array.from(allTags)?.length > 5 && (
                              <Tag
                                value={
                                  <div className="d-flex align-items-center">
                                    <Plus
                                      className="gray-txt-15 mr-1"
                                      width={13}
                                      height={13}
                                    />
                                    <p className="mb-0">
                                      {Array.from(allTags)?.length - 5}
                                    </p>
                                  </div>
                                }
                                className="filter-tag more-tag"
                              />
                            )}
                          </div>
                        )}
                      </Accordion.ItemButton>
                    </Accordion.ItemHeading>

                    <Accordion.ItemPanel>
                      {filter?.type?.map((subFilter) => (
                        <React.Fragment
                          key={`subfilter-${filter?.id}-${subFilter.id}`}
                        >
                          {subFilter?.value ===
                            FilterComponentType.TAG_CHECKBOX && (
                            <>
                              <div>
                                {isLeadTagsLoading ? (
                                  <SkeletonLoading height={32} width={235} />
                                ) : (
                                  <TagSavedFilters
                                    leadTags={leadTags}
                                    leadTagsIds={validTagIds}
                                    onLeadTagsIdsChange={(leadTagsIds) => {
                                      let validSelectedTags = leadTagsIds;
                                      if (leadTags && leadTags?.length > 0) {
                                        const availableTagIds = leadTags?.map(
                                          (tag) => tag?.id,
                                        );
                                        validSelectedTags = leadTagsIds?.filter(
                                          (tagId) =>
                                            availableTagIds?.includes(tagId),
                                        );
                                      }

                                      setSavedFilters((prevFilters) => ({
                                        ...prevFilters,
                                        tags: validSelectedTags,
                                      }));
                                      handleQuery({
                                        filterKey: 'tags',
                                        filterValue: {
                                          tags: validSelectedTags,
                                        },
                                        filterType:
                                          FilterComponentType.TAG_CHECKBOX,
                                      });
                                    }}
                                  />
                                )}

                                {validTagIds?.length > 0 && (
                                  <div className="pill">
                                    {validTagIds?.map((tagId) => {
                                      const tagValue = leadTags.find(
                                        (ele) => ele.id === tagId,
                                      );
                                      const handlePillClose = (idToRemove) => {
                                        const updatedTags = savedFilters.tags.filter(
                                          (tag) => tag !== idToRemove,
                                        );
                                        setSavedFilters((prevFilters) => ({
                                          ...prevFilters,
                                          tags: updatedTags,
                                        }));
                                        handleQuery({
                                          filterKey: 'tags',
                                          filterValue: {
                                            tags: updatedTags,
                                          },
                                          filterType:
                                            FilterComponentType.TAG_CHECKBOX,
                                        });
                                      };

                                      if (!tagValue || !tagId) {
                                        return null;
                                      }
                                      // Render the 'name' property of the tagValue object
                                      return (
                                        <Pills
                                          key={tagId}
                                          theme="outline"
                                          size="md"
                                          className="pill-saved-filter pills-container"
                                          label={
                                            tagValue?.name?.length > 15 ? (
                                              <OverlayTooltip
                                                placement={Placement.Bottom}
                                                text={tagValue?.name}
                                              >
                                                <span>
                                                  {`${tagValue?.name?.substring(
                                                    0,
                                                    15,
                                                  )}...`}
                                                </span>
                                              </OverlayTooltip>
                                            ) : (
                                              tagValue?.name
                                            )
                                          }
                                          showCloseIcon={true}
                                          onClose={() => handlePillClose(tagId)}
                                        />
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {subFilter?.value ===
                            FilterComponentType.SAVED_LEAD_DATE && (
                            <>
                              <div className="v2-lead-filter-date-piker">
                                <DateFilter
                                  onSelect={handleDateChange}
                                  values={savedFilters?.createdDate}
                                />
                              </div>
                            </>
                          )}

                          {subFilter?.value ===
                            FilterComponentType.MULTILEVEL_CHECKBOX && (
                            <div className="bs-email-status-filter">
                              {subFilter?.inputPlaceHolder && (
                                <div className="bs-email-status-filter__label">
                                  {subFilter.inputPlaceHolder}
                                </div>
                              )}
                              <div className="bs-email-status-filter__options">
                                {subFilter?.list?.map((item: any) => {
                                  const checked = !!savedFilters?.emailStatus?.includes(
                                    item.value,
                                  );
                                  const toggle = () => {
                                    const current =
                                      savedFilters?.emailStatus || [];
                                    const next = checked
                                      ? current.filter((v) => v !== item.value)
                                      : [...current, item.value];
                                    setSavedFilters((prevFilters) => ({
                                      ...prevFilters,
                                      emailStatus: next,
                                    }));
                                    handleQuery({
                                      filterKey: 'emailStatus',
                                      filterValue: { emailStatus: next },
                                      filterType:
                                        FilterComponentType.MULTILEVEL_CHECKBOX,
                                    });
                                  };
                                  return (
                                    <div
                                      key={item.key}
                                      className="bs-email-status-filter__item"
                                      {...accessibleOnClick(toggle)}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        onChange={toggle}
                                        className="saved-list__checkbox"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="bs-email-status-filter__item-text">
                                        {item.label || item.value}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {subFilter?.value ===
                            FilterComponentType.EXCLUDE_PROSPECTS && (
                            <div
                              className="bs-exclude-prospects-filter"
                              {...accessibleOnClick(() => {
                                const nextValue = !savedFilters?.excludeProspects;
                                setSavedFilters((prevFilters) => ({
                                  ...prevFilters,
                                  excludeProspects: nextValue,
                                }));
                                handleExcludeProspectsToggle(nextValue);
                              })}
                            >
                              <div
                                {...accessibleOnClick((e) => {
                                  e.stopPropagation();
                                })}
                              >
                                <Checkbox
                                  checked={!!savedFilters?.excludeProspects}
                                  onChange={(checked) => {
                                    setSavedFilters((prevFilters) => ({
                                      ...prevFilters,
                                      excludeProspects: checked,
                                    }));
                                    handleExcludeProspectsToggle(checked);
                                  }}
                                  className="saved-list__checkbox"
                                />
                              </div>
                              <span className="saved-list__accordion-list-item-text">
                                Exclude Existing Prospects
                              </span>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </Accordion.ItemPanel>
                  </Accordion.Item>
                );
              })}
          </Accordion>
        </div>
        <SavedList
          savedListsData={savedListsData}
          allListsData={allListsData}
          onEditList={onEditList}
          onExportList={onExportList}
          onDeleteList={onDeleteList}
          onCreateList={onCreateList}
          onUpdateList={onUpdateList}
          teamsList={teamsList}
          getTeamsListRequestStatus={getTeamsListRequestStatus}
          sendGetTeamsListRequest={sendGetTeamsListRequest}
          isUpdateListLoading={isUpdateListLoading}
          handleQuery={handleQuery}
          pendingFilters={new URLSearchParams(window.location?.search)}
          activeLeadType={activeLeadType}
        />
      </div>
    </>
  );
};

export default V2SavedTab;
